// src/utils/pushSubscription.ts
/**
 * Web Push: подписка браузера и снятие подписки.
 *
 * Здесь вся логика, которая не должна жить в UI:
 *  - проверка поддержки (service worker + PushManager + Notification);
 *  - получение VAPID-ключа с сервера (или понятного «push выключен»);
 *  - разрешение на уведомления (если ещё не спрашивали);
 *  - ожидание активного service worker'а (в dev-сборке его нет);
 *  - подписка/переподписка и отправка endpoint'а на сервер;
 *  - локальное отключение + удаление подписки на сервере.
 *
 * Все функции возвращают статус и человекочитаемый текст: настройки просто
 * кладут `message` в подсказку и не разбирают исключения axios.
 */
import {
    fetchVapidPublicKey,
    isStaleSubscriptionError,
    PushSubscriptionPayload,
    registerPushSubscription,
    unregisterPushSubscription,
} from '../api/push';
import { apiErrorMessage } from '../api/errors';

/** Что настройки знают о push при открытии экрана. */
export type PushState = 'unsupported' | 'subscribed' | 'unsubscribed';

export type PushResultStatus =
    | 'subscribed'
    | 'unsubscribed'
    | 'unsupported'
    | 'denied'
    | 'disabled'
    | 'no-service-worker'
    | 'error';

export interface PushResult {
    ok: boolean;
    status: PushResultStatus;
    /** Готовый текст для подсказки под переключателем. */
    message: string;
}

export interface SubscribeOptions {
    /**
     * Разрешение уже получено другим путём: на Android его спрашивает
     * Capacitor LocalNotifications, поэтому `Notification.permission` в WebView
     * может остаться `denied`/`default`.
     */
    permissionGranted?: boolean;
}

/** Сколько ждём готовности service worker'а, прежде чем сказать «обновите страницу». */
const SERVICE_WORKER_READY_TIMEOUT_MS = 8000;

const MESSAGES = {
    subscribed: 'Push-уведомления включены — они придут, даже когда приложение закрыто.',
    unsubscribed: 'Push-уведомления выключены. Уведомления придут только при открытом приложении.',
    unsupported: 'Этот браузер не поддерживает push-уведомления.',
    denied:
        'Браузер не дал разрешение на уведомления. Разрешите их в настройках браузера для этого сайта.',
    disabled: 'Push пока не настроен на сервере — включится, когда администратор это сделает.',
    noServiceWorker:
        'Приложение ещё не готово принимать push. Обновите страницу и попробуйте снова.',
    generic: 'Не удалось включить push-уведомления. Попробуйте ещё раз.',
} as const;

/** Есть ли в браузере всё необходимое для Web Push. */
export const isPushSupported = (): boolean => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/** base64url → байты: в таком виде VAPID-ключ передаётся в PushManager.subscribe. */
export const urlBase64ToUint8Array = (base64Url: string): Uint8Array => {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const bytes = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) {
        bytes[index] = raw.charCodeAt(index);
    }
    return bytes;
};

const bufferToBase64Url = (value: ArrayBuffer | ArrayBufferView): string => {
    const bytes =
        value instanceof Uint8Array
            ? value
            : new Uint8Array(value instanceof ArrayBuffer ? value : value.buffer);
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return window
        .btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

/** Приводим подписку к телу запроса POST /v1/push/subscriptions. */
const subscriptionToPayload = (
    subscription: PushSubscription
): PushSubscriptionPayload | null => {
    const json = typeof subscription.toJSON === 'function' ? subscription.toJSON() : undefined;
    const endpoint = (json && json.endpoint) || subscription.endpoint;
    const keys = (json && json.keys) || {};

    const readKey = (name: 'p256dh' | 'auth'): string => {
        const fromJson = keys[name];
        if (typeof fromJson === 'string' && fromJson.trim() !== '') return fromJson;
        const fromBrowser = subscription.getKey(name);
        return fromBrowser ? bufferToBase64Url(fromBrowser) : '';
    };

    const p256dh = readKey('p256dh');
    const auth = readKey('auth');
    if (!endpoint || !p256dh || !auth) return null;

    return { endpoint, keys: { p256dh, auth } };
};

/** Ждём активный service worker, но не бесконечно: в dev его не регистрируют вовсе. */
const waitForServiceWorker = (): Promise<ServiceWorkerRegistration | null> => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return Promise.resolve(null);
    }
    let ready: Promise<ServiceWorkerRegistration>;
    try {
        ready = navigator.serviceWorker.ready;
    } catch (error) {
        return Promise.resolve(null);
    }
    if (!ready || typeof ready.then !== 'function') {
        return Promise.resolve(null);
    }

    return new Promise((resolve) => {
        let settled = false;
        const finish = (registration: ServiceWorkerRegistration | null) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            resolve(registration);
        };
        const timer = window.setTimeout(() => finish(null), SERVICE_WORKER_READY_TIMEOUT_MS);
        ready.then(
            (registration) => finish(registration || null),
            () => finish(null)
        );
    });
};

/** Есть ли у браузера активная подписка (без обращения к серверу). */
export const getPushState = async (): Promise<PushState> => {
    if (!isPushSupported()) return 'unsupported';
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return 'unsubscribed';
        const subscription = await registration.pushManager.getSubscription();
        return subscription ? 'subscribed' : 'unsubscribed';
    } catch (error) {
        return 'unsubscribed';
    }
};

const ensurePermission = async (options: SubscribeOptions): Promise<PushResult | null> => {
    if (options.permissionGranted) return null;
    const { Notification } = window;
    if (!Notification) return { ok: false, status: 'denied', message: MESSAGES.denied };

    if (Notification.permission === 'granted') return null;
    if (Notification.permission === 'denied') {
        return { ok: false, status: 'denied', message: MESSAGES.denied };
    }
    try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            return { ok: false, status: 'denied', message: MESSAGES.denied };
        }
        return null;
    } catch (error) {
        return { ok: false, status: 'denied', message: MESSAGES.denied };
    }
};

const describeSubscribeError = (error: unknown): PushResult => {
    const name = typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined;
    if (name === 'NotAllowedError') {
        return { ok: false, status: 'denied', message: MESSAGES.denied };
    }
    if (name === 'NotSupportedError') {
        return { ok: false, status: 'unsupported', message: MESSAGES.unsupported };
    }
    return { ok: false, status: 'error', message: MESSAGES.generic };
};

const createSubscription = async (
    registration: ServiceWorkerRegistration,
    applicationServerKey: Uint8Array
): Promise<PushSubscription> => {
    return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
    });
};

/**
 * Включить push: разрешение → key → service worker → подписка → сервер.
 *
 * Возвращает готовый текст подсказки; исключений наружу не бросает.
 */
export const subscribeToPush = async (options: SubscribeOptions = {}): Promise<PushResult> => {
    if (!isPushSupported()) {
        return { ok: false, status: 'unsupported', message: MESSAGES.unsupported };
    }

    const permissionFailure = await ensurePermission(options);
    if (permissionFailure) return permissionFailure;

    const vapid = await fetchVapidPublicKey();
    if (vapid.status === 'disabled') {
        return { ok: false, status: 'disabled', message: MESSAGES.disabled };
    }
    if (vapid.status !== 'ok' || !vapid.publicKey) {
        return {
            ok: false,
            status: 'error',
            message: 'Не удалось получить настройки push с сервера. Проверьте связь и попробуйте снова.',
        };
    }

    const registration = await waitForServiceWorker();
    if (!registration) {
        return { ok: false, status: 'no-service-worker', message: MESSAGES.noServiceWorker };
    }

    const saveSubscription = async (
        subscription: PushSubscription
    ): Promise<PushResult> => {
        const payload = subscriptionToPayload(subscription);
        if (!payload) return { ok: false, status: 'error', message: MESSAGES.generic };
        try {
            await registerPushSubscription(payload);
            return { ok: true, status: 'subscribed', message: MESSAGES.subscribed };
        } catch (error) {
            if (isStaleSubscriptionError(error)) {
                // Подписка уже недействительна: снимаем локально, чтобы
                // следующая попытка создала новую.
                try {
                    await subscription.unsubscribe();
                } catch (unsubscribeError) {
                    // Браузер мог уже забыть подписку — это не мешает переподписке.
                }
                return { ok: false, status: 'error', message: MESSAGES.generic };
            }
            return {
                ok: false,
                status: 'error',
                message: apiErrorMessage(
                    error,
                    'Не удалось сохранить подписку на push на сервере. Попробуйте ещё раз.'
                ),
            };
        }
    };

    try {
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
            const saved = await saveSubscription(existing);
            if (saved.ok) return saved;
            // Старую подписку только что сняли — создаём новую ниже.
            const refreshed = await registration.pushManager.getSubscription();
            if (refreshed) return saved;
        }

        const created = await createSubscription(
            registration,
            urlBase64ToUint8Array(vapid.publicKey)
        );
        return await saveSubscription(created);
    } catch (error) {
        return describeSubscribeError(error);
    }
};

/**
 * Выключить push: снять подписку в браузере и сообщить серверу.
 *
 * Если сервер не ответил — не страшно: локальной подписки уже нет, а свою
 * копию бэкенд выбросит по 410 от push-службы.
 */
export const unsubscribeFromPush = async (): Promise<PushResult> => {
    if (!isPushSupported()) {
        return { ok: true, status: 'unsupported', message: MESSAGES.unsupported };
    }

    let subscription: PushSubscription | null = null;
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        subscription = registration ? await registration.pushManager.getSubscription() : null;
    } catch (error) {
        subscription = null;
    }

    const endpoint = subscription ? subscription.endpoint : null;
    if (subscription) {
        try {
            await subscription.unsubscribe();
        } catch (error) {
            // Браузер мог уже потерять подписку — сервер всё равно почистим.
        }
    }
    if (endpoint) {
        try {
            await unregisterPushSubscription(endpoint);
        } catch (error) {
            // См. комментарий к функции.
        }
    }

    return { ok: true, status: 'unsubscribed', message: MESSAGES.unsubscribed };
};
